import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTranslation } from 'react-i18next';
import {
  shortcutPathToTranslationKey,
  registryPathToTranslationKey,
  entityNameToTranslationKey,
} from '../components/shortcutNavBarMaps';

export interface OpenTab {
  id: string;
  path: string;
  label: string;
  /** Entity name resolved after the screen has loaded its data. */
  labelOverride?: string;
  state?: any;
  /** Timestamp (Date.now()) of the last time this tab was activated */
  lastActiveAt: number;
}

/** Maximum number of simultaneously mounted tab instances */
const MAX_ALIVE_TABS = 12;

interface OpenTabsContextType {
  tabs: OpenTab[];
  openOrActivateTab: (path: string, label: string, state?: any) => void;
  setTabLabel: (path: string, label: string) => void;
  closeTab: (id: string) => void;
  clearTabs: () => void;
}

const OpenTabsContext = createContext<OpenTabsContextType>({
  tabs: [],
  openOrActivateTab: () => {},
  setTabLabel: () => {},
  closeTab: () => {},
  clearTabs: () => {},
});

export const useOpenTabs = () => useContext(OpenTabsContext);

// Paths that should never become open tabs (non-screen routes).
const EXCLUDED_PATHS = new Set(['/', '/login']);
const EXCLUDED_PREFIXES = ['/reset-password', '/change-password'];

function isExcluded(pathname: string): boolean {
  if (EXCLUDED_PATHS.has(pathname)) return true;
  return EXCLUDED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

// Known dynamic path prefixes → translation key for their base label.
// Only entries that already have translation keys in the app are listed here.
const DYNAMIC_PREFIX_KEY: Array<[string, string]> = [
  ['/classification/', 'tableHeaders.classification'],
  ['/material/', 'sidebar.items'],
  ['/work-order/', 'sidebar.workOrders'],
  ['/planning/', 'sidebar.planning'],
  ['/os/archive', 'sidebar.normativArchive'],
  ['/os/', 'sidebar.osBasicSpecification'],
  ['/sales-print-preview/', 'sidebar.sales'],
  ['/production-print-preview/', 'sidebar.manufacturing'],
  ['/procurement-print-preview/', 'sidebar.procurement'],
];

export function deriveLabel(pathname: string, state: any, t: (key: string) => string): string {
  // Print routes end in a template ID. Never display that ID as a tab title.
  if (/^\/[^/]*print-preview(?:\/|$)/.test(pathname)) {
    return state?.printFormName || state?.name || t('printPreview.printPreview');
  }

  // 1. Exact match in known/manual routes → translated label
  const tKey = shortcutPathToTranslationKey[pathname];
  if (tKey) return t(tKey);

  // 2. Registry route match → translated label
  const registryKey = registryPathToTranslationKey[pathname];
  if (registryKey) return t(registryKey);

  // 3. Location state carries a code or name (e.g. document screens)
  if (state?.code) return String(state.code);
  if (state?.name) return String(state.name);

  // 4. Try entityNameToTranslationKey on the last URL segment (humanised).
  const segments = pathname.split('/').filter(Boolean);
  const last = segments[segments.length - 1] ?? pathname;
  const lastHuman = last.replace(/[-_]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  const entityKey = entityNameToTranslationKey[lastHuman] ?? entityNameToTranslationKey[last];
  if (entityKey) return t(entityKey);

  // 5. Known dynamic prefixes → translated base label where a key exists.
  for (const [prefix, prefixKey] of DYNAMIC_PREFIX_KEY) {
    if (pathname.startsWith(prefix)) {
      const resolved = t(prefixKey);
      // t() returns the key itself when missing — only use if it resolved.
      if (resolved !== prefixKey) return resolved;
    }
  }

  // 6. Fallback: humanised last segment.
  return lastHuman;
}

interface OpenTabsProviderProps {
  children: ReactNode;
}

export const OpenTabsProvider: React.FC<OpenTabsProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  const [tabs, setTabs] = useState<OpenTab[]>([]);

  // Clear tabs on logout (mirrors ShortcutContext behaviour).
  useEffect(() => {
    if (!user?._id) {
      setTabs([]);
    }
  }, [user?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const openOrActivateTab = useCallback((path: string, label: string, state?: any) => {
    if (isExcluded(path.split('?')[0])) return;
    const now = Date.now();
    setTabs((prev) => {
      const existing = prev.find((tab) => tab.path === path);
      if (existing) {
        // Refresh label, state, and lastActiveAt on re-activation.
        return prev.map((tab) =>
          tab.path === path ? { ...tab, label, state, lastActiveAt: now } : tab
        );
      }
      const newTab: OpenTab = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        path,
        label,
        state,
        lastActiveAt: now,
      };
      const withNew = [...prev, newTab];
      // LRU cap: if we've exceeded MAX_ALIVE_TABS, evict the oldest inactive tab.
      if (withNew.length > MAX_ALIVE_TABS) {
        // Find the oldest tab that is NOT the newly added (currently active) one.
        let oldestIdx = -1;
        let oldestTime = Infinity;
        for (let i = 0; i < withNew.length; i++) {
          const t = withNew[i];
          if (t.path !== path && t.lastActiveAt < oldestTime) {
            oldestTime = t.lastActiveAt;
            oldestIdx = i;
          }
        }
        if (oldestIdx !== -1) {
          return withNew.filter((_, i) => i !== oldestIdx);
        }
      }
      return withNew;
    });
  }, []);

  const setTabLabel = useCallback((path: string, label: string) => {
    setTabs((prev) => {
      const tab = prev.find((entry) => entry.path === path);
      if (!tab || tab.labelOverride === label) return prev;
      return prev.map((entry) => entry.path === path ? { ...entry, labelOverride: label } : entry);
    });
  }, []);

  const closeTab = useCallback((id: string) => {
    setTabs((prev) => prev.filter((tab) => tab.id !== id));
  }, []);

  const clearTabs = useCallback(() => {
    setTabs([]);
  }, []);

  // Track navigation: open or activate a tab on every route change.
  // location.key is included so that re-navigating to the SAME path with new
  // location.state (e.g. re-opening a print preview with changed filter criteria)
  // still refreshes the open tab's stored state. Without it, pathname/search are
  // unchanged for same-path re-navigation, the effect never fires, and the
  // kept-alive screen keeps rendering its original (stale) state until F5.
  useEffect(() => {
    const { pathname } = location;
    if (isExcluded(pathname)) return;
    if (location.state?.skipOpenTab) return;

    const fullPath = `${location.pathname}${location.search}`;
    const label = deriveLabel(pathname, location.state, t);
    openOrActivateTab(fullPath, label, location.state);
  }, [location.pathname, location.search, location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <OpenTabsContext.Provider
      value={{
        tabs,
        openOrActivateTab,
        setTabLabel,
        closeTab,
        clearTabs,
      }}
    >
      {children}
    </OpenTabsContext.Provider>
  );
};
